import { Box, Chip, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

// others
import { CASE_DETAIL_ROUTE, NOT_APPLICABLE } from '../../../constants'

// components
import { format } from 'date-fns'
import dayjs from 'dayjs'
import { PaymentListingProps } from '../../../interfaceTypes/props'
import { titleize } from '../../../utils'
import { CustomPagination } from '../../common/CustomPagination'
import { CustomTableCell } from '../../common/CustomTableCell'
import { StickyTable } from '../../common/StickyTable'
import PaymentsMenu from '../../menus/Payments'
import { TableSkeleton } from '../../skeletons/TableSkeleton'

// others
import { PaymentSource, PaymentType } from '../../../../generated'
import { ProcessedPayment } from '../../../interfaceTypes/common'

/** Same amber the payables listing uses for a row that needs noticing rather than a row in error. */
const REFUND_TAG_COLOR = '#ED6C02'

/**
 * Firm payments are the only deposit type the API can reverse from this listing today. Every other
 * condition the API rejects is mirrored here so the action is never offered when it cannot succeed:
 * reconciled payments, already-returned checks, and Advantage-migrated firm payments (which carry no
 * journal entry of their own, so there is no GL side to reverse).
 */
export const canDeletePayment = (payment: ProcessedPayment, isAdmin: boolean): boolean =>
  isAdmin &&
  payment.paymentSource === PaymentSource.FirmPayment &&
  !payment.reconciledAt &&
  !payment.returnedAt &&
  !payment.fromAdv

interface PaymentsListingWithEditProps extends PaymentListingProps {
  onEdit: (paymentId: string) => void
  onView: (paymentId: string) => void
}

const formatPaymentDate = (value: unknown): string => {
  if (value == null || value === '') return NOT_APPLICABLE
  // The API returns `createdAt` as an epoch-millisecond string and `date` as
  // an ISO string. `new Date(numericString)` is Invalid Date, so coerce
  // digit-only strings to a number before constructing the Date.
  let input: string | number | Date = value as string | number | Date
  if (typeof input === 'string' && /^\d+$/.test(input)) input = Number(input)
  const d = new Date(input)
  return isNaN(d.getTime()) ? NOT_APPLICABLE : format(d, 'MM/dd/yyyy')
}

// A returned (NSF) check keeps its original value in `returnAmount` while `amount` is zeroed,
// so display the original amount with a "Returned" marker instead of a misleading $0.00.
const formatPaymentAmount = (payment: ProcessedPayment): string => {
  if (payment.returnedAt) {
    return `$${(payment.returnAmount ?? 0).toFixed(2)} (Returned)`
  }
  return `$${payment.amount.toFixed(2)}`
}

/**
 * Pay Date cell that tags refunds.
 *
 * Refunds are recorded against a client and a matter with no vendor, so they land in the client tab
 * next to ordinary client payments and read exactly like one — same columns, same positive amount,
 * the only difference being a bank account the money left rather than entered. The tag rides in the
 * Pay Date cell instead of a column of its own: the client table already carries fourteen.
 */
const PaymentDateCell = ({ payment }: { payment: ProcessedPayment }) => {
  const isRefund = payment.paymentSource === PaymentSource.RefundPayment

  return (
    <TableCell>
      <Box maxWidth="376px" display="flex" alignItems="center" gap={1}>
        <Typography variant="body1" noWrap color="black">
          {formatPaymentDate(payment.date)}
        </Typography>

        {isRefund && (
          <Chip
            size="small"
            label="Refund"
            sx={{
              color: REFUND_TAG_COLOR,
              border: `1px solid ${REFUND_TAG_COLOR}50`,
              backgroundColor: 'transparent'
            }}
          />
        )}
      </Box>
    </TableCell>
  )
}

const PaymentsListing = ({ loading, paymentData, onEdit, onView }: PaymentsListingWithEditProps) => {
  const { headers, component } = paymentData

  return (
    <>
      <StickyTable pagination={<CustomPagination loading={loading || false} />} height="460px">
        <TableHead>
          <TableRow hover>
            {headers.map((columnName, index) => (
              <TableCell key={index} component="th" align={'left'}>
                {columnName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {loading ? <TableSkeleton noOfRows={7} noOfCols={headers.length} /> : <TableBody>{component}</TableBody>}
      </StickyTable>
    </>
  )
}

export const ClientPayments = ({
  payments,
  onEdit,
  onView
}: {
  payments: ProcessedPayment[]
  onEdit: (paymentId: string) => void
  onView: (paymentId: string) => void
}) => {
  return (
    <>
      {payments?.map(payment => {
        const {
          id,
          createdAt,
          sourceAccount,
          destinationAccount,
          referenceNumber,
          bill,
          case: matter,
          client,
          notes,
          slipNumber,
          office,
          reconciledAt
        } = payment || {}
        const { name: sourceAccountName, id: sourceId } = sourceAccount || {}
        const { name: destinationAccountName, id: destinationId } = destinationAccount || {}
        const { caseNumber, id: caseId } = matter || {}
        const { id: billId } = bill || {}

        return (
          <TableRow key={id} hover>
            <PaymentDateCell payment={payment} />
            <CustomTableCell value={formatPaymentDate(createdAt)} maxWidth="376px" />
            <CustomTableCell
              value={billId?.toString() || NOT_APPLICABLE}
              to={`/billing/bill-detail/${billId}`}
              maxWidth="80px"
            />
            <CustomTableCell
              value={caseNumber ? `#${caseNumber}` : NOT_APPLICABLE}
              to={caseNumber ? `${CASE_DETAIL_ROUTE}/${caseId}` : undefined}
            />
            <CustomTableCell
              value={
                client?.displayName ||
                (client?.firstName || '') +
                  ' ' +
                  (client?.middleName || '') +
                  ' ' +
                  (client?.lastName || '') +
                  ' ' +
                  (client?.suffix || '') ||
                NOT_APPLICABLE
              }
            />
            <CustomTableCell
              value={sourceAccountName || NOT_APPLICABLE}
              to={sourceAccountName ? `/accounts/chartered/${sourceId}` : undefined}
            />
            <CustomTableCell
              value={destinationAccountName || NOT_APPLICABLE}
              to={destinationAccountName ? `/accounts/chartered/${destinationId}` : undefined}
            />
            <CustomTableCell value={formatPaymentAmount(payment)} />
            <CustomTableCell value={referenceNumber || NOT_APPLICABLE} />
            <CustomTableCell value={slipNumber || NOT_APPLICABLE} />
            <CustomTableCell value={office?.name || NOT_APPLICABLE} />
            <CustomTableCell value={notes || NOT_APPLICABLE} />
            <TableCell>
              {reconciledAt ? (
                <Tooltip title={`Reconciled at: ${dayjs(reconciledAt).format('MM/DD/YYYY hh:mm A')}`} placement="top">
                  <CheckCircleIcon color="success" fontSize="small" />
                </Tooltip>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>
              <PaymentsMenu payment={payment} onEdit={onEdit} onView={onView} />
            </TableCell>
          </TableRow>
        )
      })}
    </>
  )
}

export const VendorPayments = ({
  payments,
  onEdit,
  onView
}: {
  payments: ProcessedPayment[]
  onEdit: (paymentId: string) => void
  onView: (paymentId: string) => void
}) => {
  return (
    <>
      {payments?.map(payment => {
        const {
          id,
          date,
          createdAt,
          paymentType,
          paymentMethod,
          sourceAccount,
          destinationAccount,
          referenceNumber,
          vendor,
          office,
          notes,
          reconciledAt
        } = payment || {}
        const { name: sourceAccountName, id: sourceId } = sourceAccount || {}
        const { name: destinationAccountName, id: destinationId } = destinationAccount || {}
        const isDebitPayment = paymentType === PaymentType.Debit
        return (
          <TableRow key={id} hover>
            <CustomTableCell value={formatPaymentDate(date)} maxWidth="376px" />
            <CustomTableCell value={formatPaymentDate(createdAt)} maxWidth="376px" />
            <CustomTableCell value={titleize(vendor?.name || NOT_APPLICABLE)} />
            <CustomTableCell value={titleize(paymentType)} />
            <CustomTableCell value={titleize(paymentMethod)} />
            <CustomTableCell
              value={(isDebitPayment ? sourceAccountName : destinationAccountName) || NOT_APPLICABLE}
              to={
                isDebitPayment
                  ? sourceAccountName
                    ? `/accounts/chartered/${sourceId}`
                    : undefined
                  : destinationAccountName
                  ? `/accounts/chartered/${destinationId}`
                  : undefined
              }
            />

            <CustomTableCell value={formatPaymentAmount(payment)} />
            <CustomTableCell value={referenceNumber || NOT_APPLICABLE} />
            <CustomTableCell value={office?.name || NOT_APPLICABLE} />
            <CustomTableCell value={notes || NOT_APPLICABLE} />

            <TableCell>
              {reconciledAt ? (
                <Tooltip title={`Reconciled at: ${dayjs(reconciledAt).format('MM/DD/YYYY hh:mm A')}`} placement="top">
                  <CheckCircleIcon color="success" fontSize="small" />
                </Tooltip>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>
              <PaymentsMenu payment={payment} onEdit={onEdit} onView={onView} />
            </TableCell>
          </TableRow>
        )
      })}
    </>
  )
}

export const FirmPayments = ({
  payments,
  onEdit,
  onView,
  onDelete,
  isAdmin
}: {
  payments: ProcessedPayment[]
  onEdit: (paymentId: string) => void
  onView: (paymentId: string) => void
  onDelete: (paymentId: string) => void
  isAdmin: boolean
}) => {
  return (
    <>
      {payments?.map(payment => {
        const {
          id,
          date,
          createdAt,
          paymentType,
          paymentMethod,
          destinationAccount,
          referenceNumber,
          office,
          notes,
          reconciledAt,
          vendor,
          case: matter
        } = payment || {}
        const { name: destinationAccountName, id: destinationId } = destinationAccount || {}
        const { caseNumber, id: caseId } = matter || {}
        return (
          <TableRow key={id} hover>
            <CustomTableCell value={formatPaymentDate(date)} maxWidth="376px" />
            <CustomTableCell value={formatPaymentDate(createdAt)} maxWidth="376px" />
            <CustomTableCell value={titleize(vendor?.name || NOT_APPLICABLE)} />
            <CustomTableCell
              value={caseNumber ? `#${caseNumber}` : NOT_APPLICABLE}
              to={caseNumber ? `${CASE_DETAIL_ROUTE}/${caseId}` : undefined}
            />
            <CustomTableCell value={titleize(paymentType)} />
            <CustomTableCell value={titleize(paymentMethod)} />
            <CustomTableCell
              value={destinationAccountName || NOT_APPLICABLE}
              to={destinationAccountName ? `/accounts/chartered/${destinationId}` : undefined}
            />
            <CustomTableCell value={formatPaymentAmount(payment)} />
            <CustomTableCell value={referenceNumber || NOT_APPLICABLE} />
            <CustomTableCell value={office?.name || NOT_APPLICABLE} />
            <CustomTableCell value={notes || NOT_APPLICABLE} />
            <TableCell>
              {reconciledAt ? (
                <Tooltip title={`Reconciled at: ${dayjs(reconciledAt).format('MM/DD/YYYY hh:mm A')}`} placement="top">
                  <CheckCircleIcon color="success" fontSize="small" />
                </Tooltip>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>
              <PaymentsMenu
                payment={payment}
                onEdit={onEdit}
                onView={onView}
                onDelete={onDelete}
                canDelete={canDeletePayment(payment, isAdmin)}
              />
            </TableCell>
          </TableRow>
        )
      })}
    </>
  )
}

export default PaymentsListing
